import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { Modal } from './comps';
import { FormikProvider, useFormik } from 'formik';
import { Poppins, Spacer } from 'src/common';
import Button from 'src/components/form/Button';
import { Guard } from './Guard';
import useOrgSwitch from 'src/hooks/useWorkspaceSwitch';
import { useCache } from 'src/graphql/useCache';
import { useUpsertWorkspaceMutation } from 'src/graphql/_generated';
import {
  WorkspaceForm,
  createWorkspaceValidationSchema,
  workspaceInitialValues,
  WorkspaceFormValues,
} from 'src/components/formComposed/WorkspaceForm';
import { useAuth } from 'src/state/auth';
import { showSuccessToast, toastMessages } from 'src/components/Toast';
import { setPendingOrgTableItem } from 'src/components/OrgTable/useScrollToNewItem';

const StyledModal = styled(Modal)`
  width: 566px;
  padding: 0 !important;
  max-height: calc(100% - 40px);
  overflow-y: auto;

  .btns {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-gap: 20px;
  }

  .error {
    color: ${({ theme }) => theme.colors.error};
    font-size: 14px;
    text-align: center;
  }
`;

interface WorkspaceCreateModalProps {
  onClose: () => void;
}

export const WorkspaceCreateModal: React.FC<WorkspaceCreateModalProps> = ({ onClose }) => {
  const [err, setErr] = useState('');
  const [isCloseRequested, setIsCloseRequested] = useState(false);
  const { refetchQuery } = useCache();
  const { flatOrganisations } = useAuth();

  const validationSchema = useMemo(() => createWorkspaceValidationSchema(flatOrganisations || []), [flatOrganisations]);

  const { isLoading: isLoadingSwitch } = useOrgSwitch({
    onError: (err) => setErr(err.message || 'Something went wrong'),
  });

  const [create] = useUpsertWorkspaceMutation({
    onError: (err) => setErr(err.message || 'Something went wrong'),
  });

  const formik = useFormik<WorkspaceFormValues>({
    initialValues: workspaceInitialValues,
    validationSchema,
    validateOnMount: true,
    enableReinitialize: true,
    onSubmit: async (values) => {
      const result = await create({ variables: { ...values, isRestricted: !!values.isRestricted } });
      if (result.data?.upsertWorkspace?.id) {
        setPendingOrgTableItem(result.data.upsertWorkspace.id);
      }
      await refetchQuery({ include: ['profile', 'flatOrganisationsHome', 'flatOrganisationsNav'] });
      showSuccessToast(toastMessages.workspace.created);
      onClose();
    },
  });

  const { isSubmitting, handleSubmit, isValid, dirty } = formik;

  return (
    <Guard
      onCancel={() => setIsCloseRequested(false)}
      onClose={onClose}
      isOpen={isCloseRequested}
      isDirty={dirty}
      modalComponent={
        <StyledModal onRequestClose={() => setIsCloseRequested(true)} isOpen persistent={isSubmitting}>
          <Spacer $px={40} />
          <div className="h-padding">
            <Poppins className="m-title" px={28}>
              Create a Workspace
            </Poppins>
            <Spacer $px={30} />
            <FormikProvider value={formik}>
              <WorkspaceForm />
            </FormikProvider>
            <Spacer $px={40} />
            <div className="btns">
              <Button secondary onClick={() => setIsCloseRequested(true)} primary disabled={isSubmitting}>
                CANCEL
              </Button>
              <Button
                primary
                disabled={!isValid || isSubmitting || !dirty || isLoadingSwitch}
                loading={isSubmitting}
                onClick={() => handleSubmit()}
              >
                CREATE
              </Button>
            </div>

            {!!err && (
              <>
                <Spacer $px={15} />
                <div className="error">{err}</div>
              </>
            )}
          </div>

          <Spacer $px={40} />
        </StyledModal>
      }
    />
  );
};
